/**
 * TeamChatManager
 *
 * Global manager for all open chat panels.
 * Renders up to 3 panels simultaneously, stacked from the right.
 * Must be mounted once at the App root level (inside SocketProvider + AuthProvider).
 *
 * Also exposes useTeamChatManager() hook for opening chats from anywhere.
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { TeamUserWithPresence } from '@troia-v3/types';
import { TeamChatPanel } from './TeamChatPanel';

// Build deterministic roomId (same as backend)
function buildRoomId(appId: string, companyId: string, uid1: string, uid2: string): string {
  const sorted = [uid1, uid2].sort();
  return `${appId}:${companyId}:${sorted[0]}:${sorted[1]}`;
}

interface ChatSession {
  user: TeamUserWithPresence;
  roomId: string;
  isMinimized: boolean;
}

interface TeamChatManagerContextType {
  openChat: (user: TeamUserWithPresence) => void;
  closeChat: (userId: string) => void;
  sessions: ChatSession[];
}

const TeamChatManagerContext = createContext<TeamChatManagerContextType | null>(null);

export const useTeamChatManager = () => {
  const ctx = useContext(TeamChatManagerContext);
  if (!ctx) throw new Error('useTeamChatManager must be used inside TeamChatManagerProvider');
  return ctx;
};

interface Props {
  children: ReactNode;
  currentUserId: string;
  appId: string;
  companyId: string;
}

const MAX_PANELS = 3;

export const TeamChatManagerProvider: React.FC<Props> = ({
  children,
  currentUserId,
  appId,
  companyId,
}) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);

  const openChat = useCallback(
    (user: TeamUserWithPresence) => {
      setSessions((prev) => {
        const existing = prev.find((s) => s.user.id === user.id);
        if (existing) {
          // If already open, un-minimize it
          return prev.map((s) =>
            s.user.id === user.id ? { ...s, isMinimized: false } : s
          );
        }
        const roomId = buildRoomId(appId, companyId, currentUserId, user.id);
        const newSession: ChatSession = { user, roomId, isMinimized: false };
        // If at max, remove the leftmost (oldest) panel
        const next = prev.length >= MAX_PANELS ? prev.slice(1) : prev;
        return [...next, newSession];
      });
    },
    [appId, companyId, currentUserId]
  );

  const closeChat = useCallback((userId: string) => {
    setSessions((prev) => prev.filter((s) => s.user.id !== userId));
  }, []);

  const minimizeChat = useCallback((userId: string) => {
    setSessions((prev) =>
      prev.map((s) => (s.user.id === userId ? { ...s, isMinimized: true } : s))
    );
  }, []);

  const unminimizeChat = useCallback((userId: string) => {
    setSessions((prev) =>
      prev.map((s) => (s.user.id === userId ? { ...s, isMinimized: false } : s))
    );
  }, []);

  return (
    <TeamChatManagerContext.Provider value={{ openChat, closeChat, sessions }}>
      {children}
      {/* Render panels fixed on screen — rightmost is index 0, then 1, 2 stacking left */}
      {sessions.map((session, i) => {
        const panelIndex = sessions.length - 1 - i; // rightmost = highest index session
        return (
          <TeamChatPanel
            key={session.user.id}
            targetUser={session.user}
            roomId={session.roomId}
            panelIndex={panelIndex}
            isMinimized={session.isMinimized}
            onClose={() => closeChat(session.user.id)}
            onMinimize={() => minimizeChat(session.user.id)}
            onOpen={() => unminimizeChat(session.user.id)}
          />
        );
      })}
    </TeamChatManagerContext.Provider>
  );
};
